import {toast} from 'sonner';
import {styled} from '@mui/material/styles';
import {useEffect, useRef} from 'react';
import {Typography} from '@mui/material';
import Button from '@mui/material/Button';
import {useNavigate} from 'react-router-dom';

import {pluralize} from 'utils/formatters';
import {DECISIONS_WHATS_NEW} from 'core/constants/paths';
import {useWhatsNewFiltersStore} from 'modules/decisionsNext/sections/WhatsNewSection/whatsNewPageStore';
import {useSaveSeenFeatures} from 'modules/decisionsNext/sections/WhatsNewSection/hooks/useSaveSeenFeatures';
import {useFeatureFlag} from 'core/hooks/authHooks';
import {FeatureFlags} from 'core/constants/featureFlags';
import {localStorageKey} from 'core/constants';
import {dayjs} from 'utils/dateUtils';
import {useWhatsNewData} from 'modules/decisionsNext/sections/WhatsNewSection/useWhatsNewData';
import {saveWhatsNewToastLatestSeenDate} from 'modules/decisionsNext/sections/WhatsNewSection/utils';

import WhatsNewIcon from 'components/icons/news.svg';

const SRoot = styled('div')(({theme}) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  gap: theme.spacing(3),
}));

const SDescription = styled('div')(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(2),
}));

const SIconContainer = styled('div')(() => ({
  position: 'relative',
}));

const SDot = styled('div')(({theme}) => ({
  position: 'absolute',
  top: -1,
  right: -2,

  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.error.main,
}));

const SButton = styled(Button)(({theme}) => ({
  color: theme.palette.primary.main,
  borderColor: theme.palette.primary.main,

  ['&:hover']: {
    borderColor: theme.palette.primary.main,
  },
}));

export const WHATS_NEW_TOAST_ID = 'whats-new-toast';

export function useWhatsNewToast(withDecisions: boolean) {
  const newFeaturesCount = useWhatsNewFiltersStore(store => store.newFeaturesCount);
  const hasShownRef = useRef(false);
  const withWhatsNewPage = useFeatureFlag(FeatureFlags.WhatsNew);
  const {items} = useWhatsNewData();

  useEffect(() => {
    if (!withDecisions || !withWhatsNewPage) {
      return;
    }

    const previousDateToSeeToast = localStorage.getItem(localStorageKey.WhatsNewToastLastSeenDate);
    const newFeatureLatestDate = items.reduce((latest, item) => {
      return dayjs(item.date).isAfter(dayjs(latest)) ? item.date : latest;
    }, items[0]?.date);

    if (
      previousDateToSeeToast &&
      newFeatureLatestDate &&
      dayjs(previousDateToSeeToast).isAfter(dayjs(newFeatureLatestDate))
    ) {
      return;
    }

    if (newFeaturesCount > 0 && !hasShownRef.current) {
      hasShownRef.current = true;
      toast(<Toast />, {
        id: WHATS_NEW_TOAST_ID,
        duration: Infinity,
        onDismiss: () => {
          saveWhatsNewToastLatestSeenDate();
        },
      });
    }
  }, [items, newFeaturesCount, withDecisions, withWhatsNewPage]);
}

const Toast = () => {
  const {saveSeenFeatures} = useSaveSeenFeatures();
  const newFeaturesCount = useWhatsNewFiltersStore(store => store.newFeaturesCount);
  const navigate = useNavigate();

  return (
    <SRoot>
      <SDescription>
        <SIconContainer>
          <WhatsNewIcon width={16} height={16} />
          <SDot />
        </SIconContainer>
        <Typography variant='captionBold'>What&apos;s new</Typography>
      </SDescription>
      <SButton
        variant='outlined'
        size='small'
        onClick={() => {
          toast.dismiss(WHATS_NEW_TOAST_ID);
          saveSeenFeatures();
          navigate(DECISIONS_WHATS_NEW);
        }}
      >
        Discover {newFeaturesCount} new {pluralize(newFeaturesCount, 'feature', 'features')}
      </SButton>
    </SRoot>
  );
};
